import { FC, useRef, useEffect } from 'react';
import { useScroll } from 'motion/react';
import { useInView } from 'motion/react';
import useClientSideMediaQuery from 'hooks/use-client-side-media-query';
import Scroller, { ItemProps, scrollerTypes } from '../Scroller';
import Marquee from '../Marquee';
import { Wrapper } from './ugc.styled';

interface UgcProps {
  type: scrollerTypes;
  items: ItemProps[];
  headline?: React.ReactNode;
}

const UgcScroller: FC<UgcProps> = ({ type, items, headline }) => {
  const isSmall = useClientSideMediaQuery(960);
  const container = useRef<HTMLElement>(null);
  const { scrollYProgress } = useScroll({
    target: container,
    offset: ['start end', 'end start'],
  });

  let half: number = 0;
  let firstRow: ItemProps[] = [];
  let secondRow: ItemProps[] = [];

  if (type === 'double') {
    half = Math.ceil(items.length / 2);
    firstRow = items.slice(0, half);
    secondRow = items.slice(half);
  }

  const isInView = useInView(container);
  const lastScrollY = useRef(0);
  const animationFrameId = useRef<number | undefined>(undefined);

  useEffect(() => {
    // autoscrolls the scroller component horizontally as the user scrolls the page vertically
    const handleScroll = () => {
      if (animationFrameId.current) {
        cancelAnimationFrame(animationFrameId.current);
      }
      animationFrameId.current = requestAnimationFrame(() => {
        if (!isInView || !container.current) return;

        const currentScrollY = window.scrollY;
        const scrollDelta = currentScrollY - lastScrollY.current;
        const horizontalScrollAmount = scrollDelta * 0.5;

        container.current.scrollLeft += horizontalScrollAmount;

        lastScrollY.current = currentScrollY;
      });
    };
    lastScrollY.current = window.scrollY;

    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (animationFrameId.current) {
        cancelAnimationFrame(animationFrameId.current);
      }
    };
  }, [isInView]);
  useEffect(() => {
    if (isSmall && container.current) {
      container.current.scrollLeft = 250;
    }
  }, [isSmall]);

  return type === 'simple' ? (
    <>
      {headline && headline}
      <Wrapper ref={container}>
        <Scroller
          items={items}
          direction='left'
          left={isSmall ? '-100%' : '-10%'}
          progress={scrollYProgress}
          type='simple'
        />
      </Wrapper>
    </>
  ) : (
    <>
      {headline && headline}
      <Wrapper ref={container}>
        <Scroller
          items={firstRow}
          direction='right'
          left={isSmall ? '-100%' : '-10%'}
          progress={scrollYProgress}
          type='double'
        />
        <Scroller
          items={secondRow}
          direction='left'
          left={isSmall ? '-100%' : '-10%'}
          progress={scrollYProgress}
          type='double'
        />
      </Wrapper>
    </>
  );
};

const UgcMarquee: FC<UgcProps> = ({ type, items, headline }) => {
  let half: number = 0;
  let firstRow: ItemProps[] = [];
  let secondRow: ItemProps[] = [];

  if (type === 'double') {
    half = Math.ceil(items.length / 2);
    firstRow = items.slice(0, half);
    secondRow = items.slice(half);
  }

  return type === 'simple' ? (
    <>
      {headline && headline}
      <Wrapper>
        <Marquee items={items} type='simple' direction='left' />
      </Wrapper>
    </>
  ) : (
    <>
      {headline && headline}
      <Wrapper>
        <Marquee items={firstRow} type='double' direction='left' />
        <Marquee items={secondRow} type='double' direction='right' />
      </Wrapper>
    </>
  );
};

export { UgcScroller, UgcMarquee };
